/**
 * LoadingSkeleton Component
 *
 * Skeleton loader with pulse animation for loading states.
 * Supports multiple variants: text, circle, and rectangle.
 *
 * CRITICAL CONSTRAINTS:
 * - Tailwind CSS core utilities only (NO custom compilation)
 * - TypeScript strict mode (NO 'any' types)
 * - Uses Tailwind animate-pulse utility
 */

import React from 'react';

export interface LoadingSkeletonProps {
  variant?: 'text' | 'circle' | 'rectangle';
  width?: string;
  height?: string;
  className?: string;
}

/**
 * LoadingSkeleton Component
 *
 * @param variant - Skeleton shape (default: 'text')
 * @param width - Width (Tailwind class or custom, default: 'w-full')
 * @param height - Height (Tailwind class or custom, default varies by variant)
 * @param className - Additional Tailwind classes
 */
export function LoadingSkeleton({
  variant = 'text',
  width,
  height,
  className = '',
}: LoadingSkeletonProps): React.JSX.Element {
  // Base classes (applied to all skeletons)
  const baseClasses = 'bg-gray-300 animate-pulse';

  // Variant-specific classes
  const variantClasses: Record<string, string> = {
    text: 'rounded h-4',
    circle: 'rounded-full',
    rectangle: 'rounded-md',
  };

  // Default dimensions if not provided
  const defaultWidth: Record<string, string> = {
    text: width || 'w-full',
    circle: width || 'w-12',
    rectangle: width || 'w-full',
  };

  const defaultHeight: Record<string, string> = {
    text: height || 'h-4',
    circle: height || 'h-12',
    rectangle: height || 'h-32',
  };

  // Combine classes
  const skeletonClasses = `${baseClasses} ${variantClasses[variant]} ${defaultWidth[variant]} ${defaultHeight[variant]} ${className}`;

  return <div className={skeletonClasses} aria-label="Loading..." role="status"></div>;
}

/**
 * LoadingSkeletonGroup Component
 *
 * Renders multiple text skeletons (useful for paragraph loading states)
 */
export interface LoadingSkeletonGroupProps {
  lines?: number;
  className?: string;
}

export function LoadingSkeletonGroup({
  lines = 3,
  className = '',
}: LoadingSkeletonGroupProps): React.JSX.Element {
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <LoadingSkeleton
          key={index}
          variant="text"
          width={index === lines - 1 ? 'w-3/4' : 'w-full'}
        />
      ))}
    </div>
  );
}
